import type { BoardGenerationJob, BoardGenerationSettings, BoardSource, Character, ExportScope, FilmProject, ImageQualitySetting, ImageSizeSetting, Scene, StoryboardFrame } from "../types";
import type { MissingInput } from "../utils/analysisSummary";
import { IMAGE_QUALITY_OPTIONS, IMAGE_SIZE_OPTIONS, resolveImageSize } from "../utils/easyMode";
import { formatRuntime, framesForScope, scenesForScope } from "../utils/storyboard";
import { Icon } from "./Icon";

interface GenerateBoardsSheetProps {
  project: FilmProject;
  settings: BoardGenerationSettings;
  onChange: (settings: BoardGenerationSettings) => void;
  onClose: () => void;
  onGenerate: () => void;
  isGenerating: boolean;
  selectedSceneId: string;
  jobs: BoardGenerationJob[];
  onRetryJob: (jobId: string) => void;
  onUploadInstead: (jobId: string) => void;
  onFixPreflight: (item: MissingInput) => void;
  onFixScript: () => void;
}

type PreflightState = "ready" | "warning" | "missing";

interface PreflightItem {
  id: string;
  label: string;
  detail: string;
  state: PreflightState;
  actionLabel?: string;
  missingInput?: MissingInput;
  onAction?: () => void;
}

const sourceOptions: Array<{
  id: BoardSource;
  label: string;
  description: string;
  badge: string;
}> = [
  {
    id: "chatgpt_image",
    label: "ChatGPT Image",
    description: "OpenAI image boards for empty slots",
    badge: "Recommended"
  },
  {
    id: "internal_svg",
    label: "Internal SVG sketch",
    description: "Free instant placeholders, swap to high-quality later",
    badge: "Free"
  },
  {
    id: "none",
    label: "Use uploaded / locked boards",
    description: "No generation, keep exactly what you placed",
    badge: "No cost"
  }
];

function sourceLabel(source: BoardSource): string {
  if (source === "chatgpt_image") return "ChatGPT Image";
  if (source === "internal_svg") return "Internal SVG";
  return "Uploaded / locked";
}

function statusLabel(job: BoardGenerationJob): string {
  if (job.status === "queued") return "Queued";
  if (job.status === "generating") return "Generating";
  if (job.status === "failed") return "Needs retry";
  return "Saved";
}

function countLabel(source: BoardSource): string {
  if (source === "internal_svg") return "saved";
  if (source === "none") return "prepared";
  return "generated";
}

function eligibleFramesForSource(source: BoardSource, scopedScenes: FilmProject["scenes"], affectedFrames: StoryboardFrame[]): StoryboardFrame[] {
  const eligible = affectedFrames.filter((frame) => !frame.locked && frame.source !== "uploaded");
  if (source !== "chatgpt_image") return eligible;
  return scopedScenes
    .map((scene) => eligible.find((frame) => frame.sceneId === scene.id))
    .filter((frame): frame is StoryboardFrame => Boolean(frame));
}

function compactNames(values: string[], fallback: string): string {
  const unique = [...new Set(values.filter(Boolean))];
  if (!unique.length) return fallback;
  const visible = unique.slice(0, 3).join(", ");
  return unique.length > 3 ? `${visible} +${unique.length - 3}` : visible;
}

function characterMissingInput(
  character: Character,
  kind: string,
  sceneId: string | undefined,
  label: string,
  fixRoute: "characters" | "voices"
): MissingInput {
  return {
    id: `${kind}-${character.name.toLowerCase().replace(/[^a-z0-9]+/g, "-") || "character"}`,
    label,
    kind,
    required: true,
    sceneId,
    characterName: character.name,
    fixRoute,
    focusId: character.id
  };
}

function sceneMissingInput(scene: Scene, kind: string, label: string, fixRoute: "references" | "generate_boards"): MissingInput {
  return {
    id: `${kind}-${scene.id || "scene"}`,
    label,
    kind,
    required: true,
    sceneId: scene.id,
    fixRoute,
    focusId: scene.id
  };
}

function generationModeSummary(source: BoardSource): { label: string; detail: string; tone: string } {
  if (source === "chatgpt_image") {
    return {
      label: "ChatGPT Image",
      detail: "Uses gpt-image-2 for eligible empty boards only. Uploads, locks, disabled boards, and protected chunks stay untouched.",
      tone: "openai"
    };
  }
  if (source === "internal_svg") {
    return {
      label: "Internal sketch",
      detail: "Creates instant storyboard placeholders in-browser/server fallback style. No paid image or EvoLink video provider is called.",
      tone: "internal"
    };
  }
  return {
    label: "Use existing boards",
    detail: "Prepares uploaded and locked boards for review without generating new images.",
    tone: "existing"
  };
}

export function GenerateBoardsSheet({
  project,
  settings,
  onChange,
  onClose,
  onGenerate,
  isGenerating,
  selectedSceneId,
  jobs,
  onRetryJob,
  onUploadInstead,
  onFixPreflight,
  onFixScript
}: GenerateBoardsSheetProps) {
  const uploadedFrames = project.scenes.flatMap((scene) => scene.frames).filter((frame) => frame.source === "uploaded").length;
  const lockedFrames = project.scenes.flatMap((scene) => scene.frames).filter((frame) => frame.locked).length;
  const scopedScenes = scenesForScope(project, selectedSceneId, settings.scope);
  const scopedFrames = framesForScope(project, selectedSceneId, settings.scope);
  const affectedFrames = scopedFrames.filter((frame) => !frame.disabled);
  const emptyOrRegeneratable = eligibleFramesForSource(settings.source, scopedScenes, affectedFrames);
  const willGenerateCount = settings.source === "none" ? 0 : emptyOrRegeneratable.length;
  const protectedInScope = scopedFrames.filter((frame) => !frame.disabled && (frame.locked || frame.source === "uploaded")).length;
  const disabledInScope = scopedFrames.filter((frame) => frame.disabled).length;
  const queuedInScope = affectedFrames.filter((frame) => frame.generationStatus === "queued" || frame.generationStatus === "generating" || frame.queuedRegenerate).length;
  const failedInScope = affectedFrames.filter((frame) => frame.generationStatus === "failed").length;
  const alreadyGeneratedInScope = affectedFrames.filter((frame) => frame.generationStatus === "generated" || frame.generationStatus === "saved" || frame.source === "generated").length;
  const estimatedSeconds = scopedScenes.reduce((total, scene) => total + scene.durationSeconds, 0);
  const recentJobs = jobs.slice(0, 4);
  const modeSummary = generationModeSummary(settings.source);
  const scopedSceneCharacterNames = [...new Set(scopedScenes.flatMap((scene) => scene.characters))];
  const scopedCharacters = project.characters.filter((character) => scopedSceneCharacterNames.includes(character.name));
  const firstScopedSceneId = scopedScenes[0]?.id || selectedSceneId;
  const missingCardCharacters = scopedCharacters.filter((character) => character.cardStatus === "missing" || character.cardDisabled);
  const missingVoiceCharacters = scopedCharacters.filter((character) => character.voiceStatus === "missing" && !character.voiceDisabled);
  const missingReferenceScenes = scopedScenes.filter((scene) => scene.readiness === "missing_reference");
  const readyPreflightCount = [
    Boolean(project.scriptUpload || project.scriptExcerpt.trim()),
    scopedScenes.length > 0,
    missingCardCharacters.length === 0,
    missingReferenceScenes.length === 0,
    missingVoiceCharacters.length === 0,
    Boolean(project.stylePreset && project.colorGrade && project.aspectRatio)
  ].filter(Boolean).length;
  const preflightItems: PreflightItem[] = [
    {
      id: "script",
      label: "Script parsed",
      detail: project.scriptUpload
        ? `${project.scriptUpload.fileName} is in the project.`
        : project.scriptExcerpt.trim()
          ? "Screenplay text is available for this storyboard pass."
          : "Upload and analyze a script before final boards.",
      state: project.scriptUpload || project.scriptExcerpt.trim() ? "ready" : "missing",
      actionLabel: "Upload script",
      onAction: project.scriptUpload || project.scriptExcerpt.trim() ? undefined : onFixScript
    },
    {
      id: "scenes",
      label: "Scenes selected",
      detail: scopedScenes.length
        ? `${scopedScenes.length} scene${scopedScenes.length === 1 ? "" : "s"} in this generation scope.`
        : "No parsed scenes are selected yet.",
      state: scopedScenes.length ? "ready" : "missing"
    },
    {
      id: "cards",
      label: "Character cards",
      detail: missingCardCharacters.length
        ? `${compactNames(missingCardCharacters.map((character) => character.name), "Character card")} need${missingCardCharacters.length === 1 ? "s" : ""} a card or Seedance source.`
        : scopedCharacters.length
          ? `${scopedCharacters.length} character source${scopedCharacters.length === 1 ? "" : "s"} ready.`
          : "No characters are assigned to these scenes.",
      state: missingCardCharacters.length ? "warning" : "ready",
      actionLabel: missingCardCharacters.length ? "Fix cards" : undefined,
      missingInput: missingCardCharacters.length
        ? characterMissingInput(
            missingCardCharacters[0],
            "character_card",
            scopedScenes.find((scene) => scene.characters.includes(missingCardCharacters[0].name))?.id || firstScopedSceneId,
            `${missingCardCharacters[0].name} character card`,
            "characters"
          )
        : undefined
    },
    {
      id: "references",
      label: "Scene references",
      detail: missingReferenceScenes.length
        ? `${compactNames(missingReferenceScenes.map((scene) => `Scene ${String(scene.number).padStart(2, "0")}`), "Scene reference")} ${missingReferenceScenes.length === 1 ? "needs" : "need"} a location/look reference.`
        : scopedScenes.length
          ? "Scene look references are ready or optional for this pass."
          : "No scenes available for references.",
      state: missingReferenceScenes.length ? "warning" : "ready",
      actionLabel: missingReferenceScenes.length ? "Fix refs" : undefined,
      missingInput: missingReferenceScenes.length
        ? sceneMissingInput(missingReferenceScenes[0], "scene_reference", `${missingReferenceScenes[0].slugline} scene reference`, "references")
        : undefined
    },
    {
      id: "voices",
      label: "Voice plan",
      detail: missingVoiceCharacters.length
        ? `${compactNames(missingVoiceCharacters.map((character) => character.name), "Voice")} need${missingVoiceCharacters.length === 1 ? "s" : ""} actor audio, Seedance voice, or no-voice.`
        : scopedCharacters.length
          ? "Voice slots are assigned or marked not needed."
          : "No speaking characters are in this scope.",
      state: missingVoiceCharacters.length ? "warning" : "ready",
      actionLabel: missingVoiceCharacters.length ? "Fix voices" : undefined,
      missingInput: missingVoiceCharacters.length
        ? characterMissingInput(
            missingVoiceCharacters[0],
            "voice_audio",
            scopedScenes.find((scene) => scene.characters.includes(missingVoiceCharacters[0].name))?.id || firstScopedSceneId,
            `${missingVoiceCharacters[0].name} voice`,
            "voices"
          )
        : undefined
    },
    {
      id: "look",
      label: "Style/look",
      detail: `${project.stylePreset || settings.stylePreset} · ${project.colorGrade || "Project grade"} · ${project.aspectRatio}`,
      state: project.stylePreset && project.colorGrade && project.aspectRatio ? "ready" : "warning"
    }
  ];
  const actionSummary = settings.source === "none"
    ? `${affectedFrames.length} board${affectedFrames.length === 1 ? "" : "s"} prepared for review; nothing is generated.`
    : `${willGenerateCount} board${willGenerateCount === 1 ? "" : "s"} will update; ${protectedInScope} protected and ${disabledInScope} disabled stay untouched.`;
  const spendSummary = settings.source === "chatgpt_image"
    ? "OpenAI image generation only · no EvoLink video spend"
    : "no provider credits";
  const readinessDetail = settings.source === "chatgpt_image"
    ? `${willGenerateCount} eligible image request${willGenerateCount === 1 ? "" : "s"} · ${protectedInScope} protected · ${failedInScope} retryable`
    : settings.source === "internal_svg"
      ? `${willGenerateCount} sketch target${willGenerateCount === 1 ? "" : "s"} · ${protectedInScope} protected · ${disabledInScope} disabled`
      : `${protectedInScope} uploaded/locked board${protectedInScope === 1 ? "" : "s"} preserved · ${affectedFrames.length} reviewed`;
  const primaryLabel = isGenerating
    ? "Generating boards"
    : settings.source === "none"
      ? `Prepare ${affectedFrames.length} boards`
      : willGenerateCount
        ? `Generate ${willGenerateCount} boards`
        : "Nothing open to generate";
  const scopeOptions: Array<{ id: ExportScope; label: string; detail: string }> = [
    { id: "current", label: "This scene", detail: "1 scene" },
    { id: "selected", label: `Selected · ${scenesForScope(project, selectedSceneId, "selected").length}`, detail: "ready scenes" },
    { id: "beats", label: `Trailer beats · ${project.agentBeats.length || 0}`, detail: `${scenesForScope(project, selectedSceneId, "beats").length} scenes` },
    { id: "all", label: `All ${project.scenes.length}`, detail: "full script" }
  ];

  return (
    <section className="generation-sheet" aria-label="Generate boards setup">
      <div className="sheet-heading">
        <div>
          <h2>Generate Boards</h2>
          <p>Storyboard frames only, never video, never EvoLink spend.</p>
        </div>
        <button className="icon-button" onClick={onClose} type="button" aria-label="Close generate boards">
          <Icon name="chevron" />
        </button>
      </div>

      <section className="preflight-card" aria-label="Generate boards preflight">
        <header>
          <div>
            <span>Preflight</span>
            <strong>{readyPreflightCount} / {preflightItems.length} ready</strong>
          </div>
          <small>Amber items can be fixed now or filled after rough boards.</small>
        </header>
        <div className="preflight-list">
          {preflightItems.map((item) => (
            <article className={`preflight-row ${item.state}`} key={item.id}>
              <span aria-hidden="true" />
              <div>
                <strong>{item.label}</strong>
                <small>{item.detail}</small>
              </div>
              {item.missingInput ? (
                <button className="secondary-action compact-action" onClick={() => {
                  if (item.missingInput) onFixPreflight(item.missingInput);
                }} type="button">
                  {item.actionLabel || "Fix"}
                </button>
              ) : item.onAction ? (
                <button className="secondary-action compact-action" onClick={item.onAction} type="button">
                  {item.actionLabel || "Fix"}
                </button>
              ) : null}
            </article>
          ))}
        </div>
      </section>

      <div className="control-group">
        <span>Scope</span>
        <div className="segmented-options">
          {scopeOptions.map((option) => (
            <button
              className={settings.scope === option.id ? "active" : ""}
              key={option.id}
              onClick={() => onChange({ ...settings, scope: option.id })}
              type="button"
            >
              <strong>{option.label}</strong>
              <small>{option.detail}</small>
            </button>
          ))}
        </div>
      </div>

      <div className="control-group">
        <span>Source</span>
        <div className="source-card-list">
          {sourceOptions.map((option) => (
            <button
              className={settings.source === option.id ? "active" : ""}
              key={option.id}
              onClick={() => onChange({ ...settings, source: option.id })}
              type="button"
            >
              <em aria-hidden="true" />
              <span>
                <strong>{option.label}</strong>
                <small>{option.description}</small>
              </span>
              <b>{option.badge}</b>
            </button>
          ))}
        </div>
      </div>

      <div className="sheet-grid compact">
        <label>
          Visual preset
          <select value={settings.stylePreset} onChange={(event) => onChange({ ...settings, stylePreset: event.target.value })}>
            <option>Cinematic Realism</option>
            <option>Graphic Noir</option>
            <option>Previs Sketch</option>
            <option>Commercial Clean</option>
          </select>
        </label>
        <label>
          Panels
          <select value={settings.panelsPerScene} onChange={(event) => onChange({ ...settings, panelsPerScene: Number(event.target.value) })}>
            {[3, 6, 9, 12].map((count) => (
              <option key={count} value={count}>{count} panels</option>
            ))}
          </select>
        </label>
        <label>
          Image quality
          <select
            value={settings.imageQuality || "low"}
            onChange={(event) => onChange({ ...settings, imageQuality: event.target.value as ImageQualitySetting })}
            disabled={settings.source !== "chatgpt_image"}
          >
            {IMAGE_QUALITY_OPTIONS.map((option) => (
              <option key={option.id} value={option.id}>{option.label}</option>
            ))}
          </select>
        </label>
        <label>
          Image size
          <select
            value={settings.imageSize || "auto"}
            onChange={(event) => onChange({ ...settings, imageSize: event.target.value as ImageSizeSetting })}
            disabled={settings.source !== "chatgpt_image"}
          >
            {IMAGE_SIZE_OPTIONS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.id === "auto" ? `${option.label} (${project.aspectRatio} → ${resolveImageSize("auto", project.aspectRatio).replace("x", "×")})` : option.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="source-rule">
        <span aria-hidden="true" />
        <p>
          Uploads and locks always win. {uploadedFrames} uploaded and {lockedFrames} locked board{lockedFrames === 1 ? "" : "s"} are kept;
          {" "}{settings.source === "none" ? "no slots are generated." : "only empty slots are generated."}
        </p>
      </div>

      <section className={`generation-readiness-card ${modeSummary.tone}`} aria-label="Generation readiness">
        <header>
          <div>
            <span>Generation readiness</span>
            <strong>{modeSummary.label}</strong>
          </div>
          <small>{readinessDetail}</small>
        </header>
        <p>{modeSummary.detail}</p>
        <div className="generation-readiness-grid">
          <div>
            <strong>{willGenerateCount}</strong>
            <span>{settings.source === "none" ? "new images" : "eligible"}</span>
          </div>
          <div>
            <strong>{protectedInScope}</strong>
            <span>protected</span>
          </div>
          <div>
            <strong>{queuedInScope}</strong>
            <span>queued</span>
          </div>
          <div>
            <strong>{failedInScope}</strong>
            <span>retry/upload</span>
          </div>
          <div>
            <strong>{alreadyGeneratedInScope}</strong>
            <span>generated</span>
          </div>
          <div>
            <strong>{disabledInScope}</strong>
            <span>disabled</span>
          </div>
        </div>
      </section>

      {recentJobs.length ? (
        <div className="generation-job-list" aria-label="Recent generation jobs">
          <span>Generation Queue</span>
          {recentJobs.map((job) => (
            <article className={`generation-job-card ${job.status}`} key={job.id}>
              <div>
                <strong>{sourceLabel(job.source)} · {statusLabel(job)}</strong>
                <small>
                  {job.frameIds.length} target · {job.generatedCount} {countLabel(job.source)} · {job.skippedCount} skipped
                </small>
                {job.error ? <p>{job.error}</p> : null}
              </div>
              {job.status === "failed" && job.source !== "none" ? (
                <div className="generation-job-actions">
                  <button className="secondary-action compact-action" disabled={isGenerating} onClick={() => onUploadInstead(job.id)} type="button">
                    Upload instead
                  </button>
                  <button className="primary-action compact-action" disabled={isGenerating} onClick={() => onRetryJob(job.id)} type="button">
                    Retry
                  </button>
                </div>
              ) : null}
            </article>
          ))}
        </div>
      ) : null}

      <details className="advanced-drawer">
        <summary>Advanced</summary>
        <div>
          <span>Chunking: auto · 10s target chunks</span>
          <span>Seed: project default</span>
          <span>Prompt details: per-frame prompts preserved</span>
          <span>Shot metadata: lens · movement · timing</span>
          <span>Image payload: ChatGPT Image uses gpt-image-2 when selected</span>
          <span>EvoLink payload: generated only in Review EvoLink dry-run</span>
        </div>
      </details>

      <div className="sheet-actions">
        <span>Est. {formatRuntime(estimatedSeconds)} · {actionSummary} · {spendSummary}</span>
        <button className="secondary-action" onClick={onClose} type="button">Cancel</button>
        <button className="primary-action" disabled={isGenerating} onClick={onGenerate} type="button">
          <Icon name="spark" />
          {primaryLabel}
        </button>
      </div>
    </section>
  );
}
